import { registerBlockType } from '@wordpress/blocks';
import { useState, useEffect, useRef } from '@wordpress/element';
import {
    useBlockProps,
    InspectorControls,
    InnerBlocks,
    PanelColorSettings,
} from '@wordpress/block-editor';
import { ComboboxControl, PanelBody, RangeControl, SelectControl } from '@wordpress/components';
import AsyncSelect from 'react-select/async';
import { useSelect, useDispatch} from '@wordpress/data';
import apiFetch from '@wordpress/api-fetch';
import './style.css';
import './editor.css';

registerBlockType('yegdigital/file-download-block', {
    title: 'File Download Block',
    icon: 'download',
    category: 'common',
    supports: {
        spacing: true, // Enables padding, margin
        color: {
            gradients: true,
            link: true,
        },
        typography: {
            fontSize: true,
            lineHeight: true,
        },
    },
    attributes: {
        selectedPost: { type: 'number', default: null },
        fileUrl: { type: 'string', default: '' },
        postTitle: { type: 'string', default: '' },
        borderColor: { type: 'string', default: '#DDDDDD' },
        borderWidth: { type: 'number', default: 1 },
        borderStyle: { type: 'string', default: 'solid' },
        padding: { type: 'number', default: 16 },
    },
    edit: ({ attributes, setAttributes, clientId }) => {
        const { selectedPost, fileUrl, postTitle, borderColor, borderWidth, borderStyle, padding } = attributes;
        const [loading, setLoading] = useState(true);
        const [options, setOptions] = useState([]); // Store fetched options
        const [page, setPage] = useState(1); // Track pagination
        const [hasMore, setHasMore] = useState(true); // Check if more posts are available
        const [searchTerm, setSearchTerm] = useState(""); // Track current search term

        // ✅ Fetch Posts with Pagination & Search Query
        const fetchPosts = async (inputValue, resetPagination = false) => {

            if (!hasMore && !resetPagination) {
                return []; // ✅ Prevent unnecessary API calls
            }

            setLoading(true);
            let currentPage = resetPagination ? 1 : page; // ✅ Reset page number on new search

            try {
                const apiUrl = `/wp/v2/file?per_page=10&page=${currentPage}&search=${inputValue}`;

                const response = await apiFetch({ path: apiUrl });

                if (response.length > 0) {
                    const newOptions = response.map((post) => ({
                        label: post.title.rendered,
                        value: post.id,
                    }));

                    if (resetPagination) {
                        setOptions(newOptions); // ✅ Reset options if it's a new search
                        setPage(2); // ✅ Start from page 2 for the next fetch
                    } else {
                        setOptions((prevOptions) => [...prevOptions, ...newOptions]); // Append new options
                        setPage(currentPage + 1); // ✅ Increment page correctly
                    }

                    return newOptions;
                } else {
                    console.log("No more posts found.");
                    setHasMore(false);
                    return [];
                }
            } catch (err) {
                console.error("Error fetching posts:", err);
                return [];
            } finally {
                setLoading(false);
            }
        };

        // ✅ Fetch First 10 Posts When Dropdown Opens
        useEffect(() => {
            fetchPosts("", true); // ✅ Reset pagination and load first 10 posts
        }, []);

        // ✅ Handle Empty Search (Show Default Options Again)
        const handleInputChange = (inputValue, { action }) => {
            if (action === "input-change") {
                setSearchTerm(inputValue); // ✅ Store search term
                if (inputValue === "") {
                    console.log("Search cleared, loading default options...");
                    fetchPosts("", true); // ✅ Reload default 10 posts when search is cleared
                }
            }
        };

        // ✅ Fetch More Posts When Scrolling to the Bottom
        const handleMenuScrollToBottom = () => {
            if (hasMore && !loading) {
                fetchPosts(searchTerm, false); // ✅ Load next 10 posts
            }
        };

        // Fetch the ACF file URL and post title when a post is selected
        const fetchPostDetails = (postId) => {
        
            apiFetch({ path: `/wp/v2/file/${postId}` })
                .then((post) => {
        
                    let fileUrl = post.acf && post.acf.resource_file ? post.acf.resource_file : "#"; // ✅ Set "#" if empty
        
                    setAttributes({
                        fileUrl: fileUrl,
                        postTitle: post.title.rendered,
                    });
                })
                .catch((err) => console.error("Error fetching post details:", err));
        };
        

        // Access inner blocks
        const { innerBlocks } = useSelect(
            (select) => ({
                innerBlocks: select('core/block-editor').getBlocks(clientId),
            }),
            [clientId]
        );

        const { updateBlockAttributes } = useDispatch('core/block-editor');

        // Update inner blocks when `postTitle` or `fileUrl` changes
        useEffect(() => {
            if (innerBlocks.length > 0) {
                const columnsBlock = innerBlocks[0];
                const columnBlocks = columnsBlock.innerBlocks;
        
                if (columnBlocks.length >= 2) {
                    // First column (Heading)
                    const headingBlock = columnBlocks[0].innerBlocks[0];
                    if (headingBlock && headingBlock.attributes.content !== postTitle) {
                        updateBlockAttributes(headingBlock.clientId, {
                            content: postTitle || 'Select a file to display title',
                        });
                    }
        
                    // Second column (Button)
                    const buttonBlock = columnBlocks[1].innerBlocks[0];
                    if (buttonBlock && typeof fileUrl === 'string') {
                        updateBlockAttributes(buttonBlock.clientId, {
                            url: fileUrl || '',
                        });
                    }
                }
            }
        }, [postTitle, fileUrl, innerBlocks]);

        const containerStyles = {
            border: `${borderWidth}px ${borderStyle} ${borderColor}`,
            padding: `${padding}px`,
        };
        

        return (
            <>
                {/* Inspector Controls */}
                <InspectorControls>
                    <PanelBody title="File Settings">
                        <AsyncSelect
                            cacheOptions
                            placeholder="Search for a file..."
                            defaultOptions={options} // ✅ Show initial 10 posts on open
                            loadOptions={(inputValue) => fetchPosts(inputValue, true)} // ✅ Reset search pagination
                            value={options.find(option => option.value === selectedPost) || null} // ✅ Keep selected value
                            onChange={(option) => {
                                setAttributes({ selectedPost: option.value }); // ✅ Save post ID
                                fetchPostDetails(option.value); // ✅ Fetch ACF field & title
                            }}
                            onInputChange={handleInputChange} // ✅ Detect when search is cleared
                            onMenuScrollToBottom={handleMenuScrollToBottom} // ✅ Infinite scrolling
                            isLoading={loading}
                            isSearchable={true}
                            isClearable={true}
                        />


                    </PanelBody>

                    <PanelColorSettings
                        title="Container Settings"
                        colorSettings={[
                            {
                                value: borderColor,
                                onChange: (color) => setAttributes({ borderColor: color }),
                                label: 'Border Color',
                            },
                        ]}
                    />

                    <PanelBody title="Border Settings" initialOpen={false}>
                        <RangeControl
                            label="Border Width"
                            value={borderWidth}
                            onChange={(value) => setAttributes({ borderWidth: value })}
                            min={0}
                            max={10}
                            step={1}
                        />
                        <SelectControl
                            label="Border Style"
                            value={borderStyle}
                            options={[
                                { label: 'Solid', value: 'solid' },
                                { label: 'Dashed', value: 'dashed' },
                                { label: 'Dotted', value: 'dotted' },
                                { label: 'Double', value: 'double' },
                                { label: 'None', value: 'none' },
                            ]}
                            onChange={(value) => setAttributes({ borderStyle: value })}
                        />
                    </PanelBody>

                    <PanelBody title="Padding Settings" initialOpen={false}>
                        <RangeControl
                            label="Padding"
                            value={padding}
                            onChange={(value) => setAttributes({ padding: value })}
                            min={0}
                            max={64}
                            step={1}
                        />
                    </PanelBody>
                </InspectorControls>

                {/* Block Content */}
                <div {...useBlockProps({ style: containerStyles })}>
                <InnerBlocks
                    template={[
                        [
                            'core/columns',
                            { 
                                className: 'file-download-columns',
                                layout: {
                                    type: 'flex',
                                    justifyContent: 'space-between',
                                    verticalAlignment: 'center',
                                },
                            },
                            [
                                [
                                    'core/column',
                                    { width: '66.66%' },
                                    [
                                        [
                                            'core/heading',
                                            {
                                                level: 3,
                                                placeholder: 'Select a file to display title',
                                                content: postTitle || 'Select a file to display title',
                                            },
                                        ],
                                    ],
                                ],
                                [
                                    'core/column',
                                    { width: '33.34%' },
                                    [
                                        [
                                            'core/buttons',
                                            {
                                                layout: true,
                                                
                                            },
                                            [
                                                [
                                                    'core/button',
                                                    {
                                                        text: 'Download File',
                                                        url: fileUrl || '',
                                                        lock: {
                                                            content: true,
                                                        },
                                                    },
                                                ],
                                            ],
                                        ],
                                    ],
                                ],
                            ],
                        ],
                    ]}
                    templateLock="all"
                />

                </div>
            </>
        );
    },
    save: ({ attributes }) => {
        const { borderColor, borderWidth, borderStyle, padding } = attributes;

        const containerStyles = {
            border: `${borderWidth}px ${borderStyle} ${borderColor}`,
            padding: `${padding}px`,
        };

        return (
            <div {...useBlockProps.save({ style: containerStyles })}>
                <InnerBlocks.Content />
            </div>
        );
    },
});
